import { RiErrorWarningLine, RiUserLine } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Input } from "@screener/uikit/components/input";
import { PanelBlock, PanelFooter } from "@screener/uikit/components/panel";
import { type FormEvent, useCallback, useState } from "react";
import { type DeleteAccountResult, deleteAccount } from "@/lib/auth-api";
import { SettingsRow } from "./settings-row";
import { SettingsSection } from "./settings-section";

export const ACCOUNT_SECTION_LABEL = "Аккаунт";

/** Что уйдёт вместе с учётной записью (§130 пп. 2, 4). */
const DELETED_WITH_ACCOUNT = [
  "настройки",
  "заметки",
  "избранное",
  "закрепления",
  "скрытые монеты",
  "личные пороги показа и время до показа",
  "правила алертов и их журнал",
  "Telegram-бот",
  "выданные приглашения",
] as const;

/** Отказы, которые раздел показывает словами; `deleted` и `session-invalid` уводят со страницы. */
export type AccountDeleteFailure = Exclude<DeleteAccountResult, "deleted" | "session-invalid">;

/**
 * Текст отказа. `switch` с `satisfies never`: новый исход `DeleteAccountResult`
 * не соберётся, а не останется без слов. Предел попыток назван иначе, чем
 * неверный пароль (как на форме входа): иначе человек подбирал бы пароль,
 * который демон уже не проверяет.
 */
export function describeDeleteFailure(failure: AccountDeleteFailure): string {
  switch (failure) {
    case "wrong-password":
      return "Неверный пароль. Учётная запись не удалена.";
    case "rate-limited":
      return "Слишком много попыток. Подождите и попробуйте снова.";
    case "owner":
      return "Учётную запись владельца из интерфейса удалить нельзя.";
    case "unavailable":
      return "Демон не отвечает. Попробуйте позже.";
    default:
      return failure satisfies never;
  }
}

export interface AccountSectionViewProps {
  /** `null` — ещё не известно (сведения `me` не получены): формы нет. */
  isOwner: boolean | null;
  password: string;
  pending: boolean;
  failure: AccountDeleteFailure | null;
  onPasswordChange: (password: string) => void;
  onSubmit: () => void;
}

/** Разметка раздела без состояния: проверяется статическим рендером. */
export function AccountSectionView({
  isOwner,
  password,
  pending,
  failure,
  onPasswordChange,
  onSubmit,
}: AccountSectionViewProps) {
  if (isOwner === null) {
    // Владелец или нет, ещё не известно (или перечтение не удалось): удаление
    // не предлагается, пока демон не сказал, что учётная запись не владельца.
    return (
      <PanelBlock>
        <p className="text-sm text-muted-foreground">Сведения об учётной записи ещё не получены.</p>
      </PanelBlock>
    );
  }
  if (isOwner) {
    return (
      <PanelBlock>
        <p className="text-sm text-muted-foreground">
          Учётную запись владельца из интерфейса удалить нельзя.
        </p>
      </PanelBlock>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="min-w-0" aria-label="Удаление">
      <PanelBlock>
        <div className="flex flex-col gap-2 text-sm">
          <p>Удаление учётной записи необратимо: восстановить её нельзя.</p>
          <p>Вместе с ней будут удалены:</p>
          <ul className="list-disc pl-5 text-muted-foreground">
            {DELETED_WITH_ACCOUNT.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </PanelBlock>
      <SettingsRow label="Текущий пароль">
        {failure ? (
          <Alert variant="error" size="sm">
            <RiErrorWarningLine aria-hidden="true" />
            <span>{describeDeleteFailure(failure)}</span>
          </Alert>
        ) : null}
        <Input
          name="password"
          type="password"
          autoComplete="current-password"
          className="max-w-[260px]"
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          disabled={pending}
        />
      </SettingsRow>
      <PanelFooter className="justify-end">
        <Button type="submit" variant="destructive" size="sm" disabled={pending || password === ""}>
          {pending ? "Удаление…" : "Удалить учётную запись"}
        </Button>
      </PanelFooter>
    </form>
  );
}

export interface AccountSectionProps {
  isOwner: boolean | null;
  /** Демон удалил учётную запись (`204`) по попытке с этим номером: клиент уходит на форму входа. */
  onDeleted: (attempt: number) => void;
  /** Сессии уже нет (`401`). */
  onSessionInvalid: () => void;
  /** Запрос удаления отправлен: закрытие потока 4401 до ответа — не «сессия истекла». Возвращает номер попытки. */
  onDeleteStart: () => number;
  /**
   * Удаление не состоялось (отказ, `401`, недоступность). `false` — попытка
   * устарела (после неё был новый вход), исход каркас не применяет.
   */
  onDeleteFailed: (attempt: number) => boolean;
}

/**
 * Раздел «Аккаунт» страницы «Настройки» (§130, design.md stage9-account-delete,
 * Д6). Подтверждение — пароль и сама кнопка, второго окна нет.
 */
export function AccountSection({
  isOwner,
  onDeleted,
  onSessionInvalid,
  onDeleteStart,
  onDeleteFailed,
}: AccountSectionProps) {
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<AccountDeleteFailure | null>(null);

  const handleSubmit = useCallback(() => {
    if (pending || password === "") {
      return;
    }
    setPending(true);
    setFailure(null);
    const attempt = onDeleteStart();
    // Колбэки каркаса вызываются и тогда, когда раздел уже размонтирован
    // (4401 пришёл раньше ответа): исход обрабатывает каркас, не раздел.
    deleteAccount(password).then((result) => {
      if (result === "deleted") {
        onDeleted(attempt);
        return;
      }
      const stillCurrent = onDeleteFailed(attempt);
      setPending(false);
      setPassword("");
      if (result === "session-invalid") {
        if (stillCurrent) {
          onSessionInvalid();
        }
        return;
      }
      setFailure(result);
    });
  }, [password, pending, onDeleted, onSessionInvalid, onDeleteStart, onDeleteFailed]);

  return (
    <SettingsSection
      sectionId="account"
      icon={<RiUserLine className="size-4" />}
      label={ACCOUNT_SECTION_LABEL}
    >
      <AccountSectionView
        isOwner={isOwner}
        password={password}
        pending={pending}
        failure={failure}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
      />
    </SettingsSection>
  );
}
